import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s) || !s) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const AudioPlaylist = ({ tracks, listen, voiceNote }) => {
  const audioRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const current = tracks[index];

  useEffect(() => {
    const a = audioRef.current;
    if (a && playing) a.play().catch(() => setPlaying(false));
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (i) => {
    const a = audioRef.current;
    if (!a) return;
    if (i === index) {
      if (playing) a.pause();
      else a.play().catch(() => {});
    } else {
      setIndex(i);
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  let lastAct = null;

  return (
    <div data-testid="lecture-audio-playlist" className="border border-line bg-parchment/60">
      <audio
        ref={audioRef}
        src={current?.src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          if (index < tracks.length - 1) {
            setIndex(index + 1);
            setPlaying(true);
          }
        }}
      />

      <div className="p-6 md:p-8 flex items-center gap-6 border-b border-line">
        <button
          data-testid="playlist-main-toggle"
          onClick={() => toggle(index)}
          aria-label={playing ? "Pause" : "Play"}
          className="shrink-0 w-16 h-16 md:w-20 md:h-20 bg-terra text-paper hover:bg-terraBright transition-colors flex items-center justify-center"
        >
          {playing ? <Pause size={26} /> : <Play size={26} className="translate-x-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-terra">{listen}</p>
          <p data-testid="playlist-current-title" className="font-display text-xl md:text-2xl text-ink mt-1 truncate">
            {current?.act} · {current?.scene}
          </p>
          <div data-testid="playlist-progress" onClick={seek} className="mt-4 h-[3px] bg-line cursor-pointer relative">
            <div
              className="absolute inset-y-0 left-0 bg-terra transition-[width] duration-150"
              style={{ width: duration ? `${(time / duration) * 100}%` : "0%" }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="font-mono2 text-[10px] text-muted2">{fmt(time)} / {fmt(duration)}</span>
            <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-muted2/70">{voiceNote}</span>
          </div>
        </div>
      </div>

      <div className="divide-y divide-line/70">
        {tracks.map((t, i) => {
          const showAct = t.act !== lastAct;
          lastAct = t.act;
          const active = i === index;
          return (
            <div key={t.src}>
              {showAct && (
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-bronze px-6 md:px-8 pt-6 pb-3">
                  {t.act}
                </p>
              )}
              <button
                data-testid={`playlist-track-${i}`}
                onClick={() => toggle(i)}
                className={`w-full flex items-center gap-5 px-6 md:px-8 py-4 text-left transition-colors ${
                  active ? "bg-terra/10" : "hover:bg-paper/70"
                }`}
              >
                <span className={`shrink-0 w-9 h-9 flex items-center justify-center border transition-colors ${
                  active ? "bg-terra text-paper border-terra" : "border-line text-ink2"
                }`}>
                  {active && playing ? <Pause size={13} /> : <Play size={13} className="translate-x-px" />}
                </span>
                <span className={`font-display text-lg md:text-xl flex-1 ${active ? "text-terra" : "text-ink"}`}>
                  {t.scene}
                </span>
                <span className="font-mono2 text-[10px] text-muted2">{fmt(t.duration)}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AudioPlaylist;
